if (!customElements.get('swatch-dropdown')) {
  customElements.define(
    'swatch-dropdown',
    class CustomSelect extends HTMLElement {
      constructor() {
        super()

        this.init()
      }

      init() {
        this.trigger = this.querySelector('.js-swatch-dropdown-trigger')
        this.optionsContainer = this.querySelector('.js-swatch-dropdown-options')
        this.selectedOptionName = this.querySelector('.js-selected-option-name')
        this.selectedOptionSubtitle = this.querySelector('.js-selected-option-subtitle')
        this.selectedOptionSwatch = this.querySelector('.js-selected-option-swatch')
        this.options = Array.from(this.querySelectorAll('.js-swatch-dropdown-option'))
        this.productInfo = this.closest('.product-container')
        this.variantRadios = this.closest('variant-radios') || this.closest('quickbuy-variant-radios')
        this.focusedOptionIndex = -1

        this.options.forEach((option, index) => {
          option.setAttribute('tabindex', '-1')
          if (!option.id) {
            option.id = `${this.dataset.productId || 'swatch'}-${this.dataset.optionPosition || 'option'}-${index}`
          }
        })

        this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this)
        this.handleOptionsKeydown = this.handleOptionsKeydown.bind(this)
        // Remove existing listeners first to prevent duplicates
        this.removeEventListeners()
        this.addEventListeners()
      }

      addEventListeners() {
        this.trigger.addEventListener('click', () => this.toggleDropdown())
        this.trigger.addEventListener('keydown', this.handleTriggerKeydown)
        this.optionsContainer.addEventListener('keydown', this.handleOptionsKeydown)
        document.addEventListener('click', (e) => this.handleClickOutside(e))
        this.options.forEach((option) => {
          option.addEventListener('click', (e) => this.handleOptionClick(e))
        })
        this.variantRadios.addEventListener('change', (e) => this.handleOptionChange(e))
      }

      removeEventListeners() {
        this.trigger.removeEventListener('click', () => this.toggleDropdown())
        this.trigger.removeEventListener('keydown', this.handleTriggerKeydown)
        this.optionsContainer.removeEventListener('keydown', this.handleOptionsKeydown)
        document.removeEventListener('click', (e) => this.handleClickOutside(e))
        this.options.forEach((option) => {
          option.removeEventListener('click', (e) => this.handleOptionClick(e))
        })
        this.variantRadios.removeEventListener('change', (e) => this.handleOptionChange(e))
      }

      handleClickOutside(e) {
        if (!this.contains(e.target)) {
          this.closeDropdown()
        }
      }

      toggleDropdown() {
        const isExpanded = this.trigger.getAttribute('aria-expanded') === 'true'
        if (isExpanded) {
          this.closeDropdown()
        } else {
          this.openDropdown()
        }
      }
      openDropdown() {
        this.trigger.setAttribute('aria-expanded', 'true')
        this.optionsContainer.hidden = false
        this.setFocusedOptionIndex(this.getSelectedOptionIndex())
        const caret = this.querySelector('.caret-down')
        if (caret) {
          caret.style.transition = 'all .3s'
          caret.style.transform = 'rotate(180deg)'
        }
      }

      closeDropdown() {
        this.trigger.setAttribute('aria-expanded', 'false')
        this.optionsContainer.hidden = true
        this.focusedOptionIndex = -1
        const caret = this.querySelector('.caret-down')
        if (caret) caret.style.transform = 'rotate(0deg)'
      }

      handleTriggerKeydown(e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          if (this.trigger.getAttribute('aria-expanded') !== 'true') {
            this.openDropdown()
          }
          this.focusOptionByOffset(e.key === 'ArrowDown' ? 1 : -1, true)
        }
      }

      handleOptionsKeydown(e) {
        if (this.trigger.getAttribute('aria-expanded') !== 'true') return

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          this.focusOptionByOffset(e.key === 'ArrowDown' ? 1 : -1)
          return
        }

        if (e.key === 'Home' || e.key === 'PageUp') {
          e.preventDefault()
          this.focusOptionAtIndex(0)
          return
        }

        if (e.key === 'End' || e.key === 'PageDown') {
          e.preventDefault()
          this.focusOptionAtIndex(this.options.length - 1)
          return
        }

        if (e.key === 'Escape') {
          e.preventDefault()
          this.closeDropdown()
          this.trigger.focus()
          return
        }

        if (e.key === 'Tab') {
          this.closeDropdown()
          return
        }

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          const focusedOption = this.options[this.focusedOptionIndex]
          if (focusedOption) {
            focusedOption.click()
          }
        }
      }

      getSelectedOptionIndex() {
        const selectedIndex = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true')
        return selectedIndex >= 0 ? selectedIndex : 0
      }

      setFocusedOptionIndex(index) {
        const normalizedIndex = Math.max(0, Math.min(index, this.options.length - 1))
        this.focusedOptionIndex = normalizedIndex
      }

      focusOptionAtIndex(index) {
        if (!this.options.length) return
        this.setFocusedOptionIndex(index)
        const option = this.options[this.focusedOptionIndex]
        option.focus()
      }

      focusOptionByOffset(offset, fromSelected = false) {
        if (!this.options.length) return
        const baseIndex =
          fromSelected || this.focusedOptionIndex < 0 ? this.getSelectedOptionIndex() : this.focusedOptionIndex
        const nextIndex = (baseIndex + offset + this.options.length) % this.options.length
        this.focusOptionAtIndex(nextIndex)
      }

      handleOptionClick(e) {
        // Prevent infinite loops from syncing
        if (this.dataset.syncing === 'true') {
          return
        }

        const value = e.currentTarget.dataset.value
        this.value = value
        // Update the trigger text
        if (!this.variantRadios) {
          this.variantRadios = this.closest('variant-radios, quickbuy-variant-radios')
        }

        let variantOption = this.variantRadios.querySelector(`[title*="${value}"]`)
        if (!variantOption) {
          const quickbuyVariantInput = this.variantRadios.querySelector(
            '.js-quickbuy-variant-single-input-for-swatch-dropdown'
          )

          if (quickbuyVariantInput) {
            quickbuyVariantInput.value = value
            quickbuyVariantInput.checked = true
            quickbuyVariantInput.dispatchEvent(new Event('change', { bubbles: true }))
          }
        }

        if (variantOption) {
          variantOption.click()
        } else {
          console.error(`No variant option found for value: ${value}`)
        }
      }

      handleOptionChange(e) {
        // Prevent infinite loops from syncing
        if (e.target.closest('swatch-dropdown')?.dataset.syncing === 'true') {
          return
        }

        let option = e.target
        if (!e.target.classList.contains('js-swatch-dropdown-option')) {
          option = this.options.find((option) => option.dataset.value.toLowerCase() === e.target.value.toLowerCase())
        }

        if (!option) return
        console.log(this.dataset)
        this.value = option.dataset.value
        if (this.dataset.recommendedVariant === this.value && this.dataset.isBundle === 'true') {
          this.querySelector('[data-dropdown-initial]').classList.remove('hidden')
        } else if (this.querySelector('[data-dropdown-initial]')) {
          this.querySelector('[data-dropdown-initial]').classList.add('hidden')
        }
        // Update main button display
        this.selectedOptionName.innerHTML = option.querySelector('.js-option-name').innerHTML
        this.selectedOptionSubtitle.textContent = option.querySelector('.js-option-subtitle').textContent
        this.selectedOptionSwatch.style.backgroundColor =
          option.querySelector('.js-option-swatch').style.backgroundColor
        this.selectedOptionSwatch.style.backgroundImage =
          option.querySelector('.js-option-swatch').style.backgroundImage

        // Update option in list
        this.options.forEach(
          (option) => option.ariaSelected === 'true' && option.setAttribute('aria-selected', 'false')
        )
        option.setAttribute('aria-selected', 'true')
        this.closeDropdown()

        // Sync with sibling dropdowns (desktop/mobile)
        this.syncSiblingDropdowns(option.dataset.value)
      }

      syncSiblingDropdowns(value) {
        const productId = this.dataset.productId
        const optionPosition = this.dataset.optionPosition

        if (!productId || !optionPosition) return

        // Find the bundle item container if it exists
        const bundleItemContainer = this.closest('.js-bundle-product-card')

        // Find all swatch-dropdowns for the same product and option
        let siblingDropdowns = document.querySelectorAll(
          `swatch-dropdown[data-product-id="${productId}"][data-option-position="${optionPosition}"]`
        )

        // If in a bundle item, only sync with dropdowns in the same bundle item
        if (bundleItemContainer) {
          siblingDropdowns = bundleItemContainer.querySelectorAll(
            `swatch-dropdown[data-product-id="${productId}"][data-option-position="${optionPosition}"]`
          )
        }

        siblingDropdowns.forEach((dropdown) => {
          // Skip self
          if (dropdown === this) return

          // Mark as syncing to prevent infinite loops
          dropdown.dataset.syncing = 'true'

          // Find the option with matching value
          const targetOption = Array.from(dropdown.querySelectorAll('.js-swatch-dropdown-option')).find(
            (opt) => opt.dataset.value === value
          )

          if (targetOption) {
            // Update the visual state
            const selectedOptionName = dropdown.querySelector('.js-selected-option-name')
            const selectedOptionSubtitle = dropdown.querySelector('.js-selected-option-subtitle')
            const selectedOptionSwatch = dropdown.querySelector('.js-selected-option-swatch')

            if (selectedOptionName && targetOption.querySelector('.js-option-name')) {
              selectedOptionName.innerHTML = targetOption.querySelector('.js-option-name').innerHTML
            }
            if (selectedOptionSubtitle && targetOption.querySelector('.js-option-subtitle')) {
              selectedOptionSubtitle.textContent = targetOption.querySelector('.js-option-subtitle').textContent
            }
            if (selectedOptionSwatch && targetOption.querySelector('.js-option-swatch')) {
              selectedOptionSwatch.style.backgroundColor =
                targetOption.querySelector('.js-option-swatch').style.backgroundColor
              selectedOptionSwatch.style.backgroundImage =
                targetOption.querySelector('.js-option-swatch').style.backgroundImage
            }

            // Update aria-selected
            dropdown.querySelectorAll('.js-swatch-dropdown-option').forEach((opt) => {
              opt.setAttribute('aria-selected', opt === targetOption ? 'true' : 'false')
            })

            // Sync dropdown option badges
            const sourceOptionBadges = this.querySelectorAll('[data-dropdown-variant]')
            sourceOptionBadges.forEach((sourceBadge) => {
              const variantId = sourceBadge.dataset.dropdownVariant
              const targetBadge = dropdown.querySelector(`[data-dropdown-variant="${variantId}"]`)
              if (targetBadge && sourceBadge.innerHTML && !sourceBadge.classList.contains('hidden')) {
                targetBadge.classList.remove('hidden')
                targetBadge.innerHTML = sourceBadge.innerHTML
              }
            })

            dropdown.value = value
          }

          // Remove syncing flag after a short delay
          setTimeout(() => {
            delete dropdown.dataset.syncing
          }, 50)
        })
      }

      // Public method to programmatically set the value (useful for on-load automatic selections)
      updateValue(value) {
        const option = this.options.find((opt) => opt.dataset.value === value)
        if (option) {
          // Simulate an option change without triggering the variant selection again
          this.value = value

          // Update the visual state
          this.selectedOptionName.innerHTML = option.querySelector('.js-option-name').innerHTML
          this.selectedOptionSubtitle.textContent = option.querySelector('.js-option-subtitle').textContent
          this.selectedOptionSwatch.style.backgroundColor =
            option.querySelector('.js-option-swatch').style.backgroundColor
          this.selectedOptionSwatch.style.backgroundImage =
            option.querySelector('.js-option-swatch').style.backgroundImage

          // Update aria-selected
          this.options.forEach((opt) => {
            opt.setAttribute('aria-selected', opt === option ? 'true' : 'false')
          })

          // Sync siblings
          this.syncSiblingDropdowns(value)
        }
      }
    }
  )
}
